import React from 'react';

// ============================================================================
// PayerBillingDetails — WHO PAID, on an inbound payment.
//
// An unattributed Stripe row (counterparty kind 'system', often with no
// lines[]) is the one somebody has to match by hand. The billing name and
// address Stripe collected sit on the payment link and were never shown; the
// API now joins them on the payer email and returns them as `payer_billing`.
//
// Shared by PaymentDetailsModal (the detail view) and LinkToBookingModal (the
// link flow), because the matching decision is made in the second one and
// checked in the first. One component so the four states below are written
// once — a second copy is a second thing to get wrong.
//
// The four states, all of which an operator will meet:
//
//   1. resolved   — one payer. Name + address.
//   2. ambiguous  — several DIFFERENT payers share this email, so the API
//                   deliberately sends NO details. Showing the wrong person's
//                   address next to a payment is worse than showing none, and
//                   this surface exists to help somebody decide. 36 of 522
//                   live joins land here; `mark@kravinskiy.com` alone has 383
//                   payment links under 9 different billing names.
//   3. no match    — we have an email but no stored payment link for it.
//   4. no payer    — a manual or wire entry. Nothing to look up, and that is
//                    not an error.
//
// Outbound payments render nothing: there is no payer on a payout.
// ============================================================================

function Line({ children }) {
  return <div>{children}</div>;
}

function PayerBillingDetails({ payment, className = '' }) {
  if (!payment || payment.direction !== 'in') return null;

  const payerEmail = payment.payer_email || '';
  const resolution = payment.payer_billing;
  const details = resolution?.billing_details;

  // 4. Manual / wire entry — no payer was ever recorded.
  if (!payerEmail) {
    return (
      <div className={className} data-testid="payer-billing">
        <div className="text-muted small">Paid by</div>
        <div className="text-muted small fst-italic" data-testid="payer-billing-none">
          No payer on file — recorded manually
        </div>
      </div>
    );
  }

  const header = (
    <div className="d-flex align-items-baseline justify-content-between gap-2">
      <div className="text-muted small">Paid by</div>
      <a
        className="small text-truncate"
        href={`mailto:${payerEmail}`}
        title={payerEmail}
      >
        {payerEmail}
      </a>
    </div>
  );

  // 2. Several different payers behind one email.
  if (resolution?.ambiguous) {
    const count = resolution.candidate_count;
    return (
      <div className={className} data-testid="payer-billing">
        {header}
        <div
          className="alert alert-warning py-1 px-2 mb-0 mt-1 small"
          role="alert"
          data-testid="payer-billing-ambiguous"
        >
          <i className="fa fa-triangle-exclamation me-1" />
          {`Can't say who paid — ${count} different payers have used this email.`}
          <div className="text-muted">
            Check the charge in Stripe before attributing this payment.
          </div>
        </div>
      </div>
    );
  }

  // 3. An email, but no payment link stored against it.
  if (!details) {
    return (
      <div className={className} data-testid="payer-billing">
        {header}
        <div className="text-muted small fst-italic" data-testid="payer-billing-unmatched">
          No stored billing details for this email
        </div>
      </div>
    );
  }

  // 1. One payer. `city` and `postcode` share a line the way an address is
  //    written; every field is optional, so empty lines never render.
  const cityLine = [details.postcode, details.city].filter(Boolean).join(' ');
  const regionLine = [details.state, details.country].filter(Boolean).join(', ');

  return (
    <div className={className} data-testid="payer-billing">
      {header}
      <div className="small" data-testid="payer-billing-resolved">
        {details.name && (
          <div className="fw-bold" data-testid="payer-billing-name">{details.name}</div>
        )}
        {details.address && <Line>{details.address}</Line>}
        {cityLine && <Line>{cityLine}</Line>}
        {regionLine && <Line>{regionLine}</Line>}
      </div>
    </div>
  );
}

export default PayerBillingDetails;
